import React, { useState, useContext } from 'react';
import { Popover, Spin, Tooltip, Tag } from 'antd';
import { colorConstants } from 'config/colorConstants';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingSimulatorUrls } from 'config/urls';
import dayjs from 'dayjs';
import { formatDatePreservingTimeValues, getConfig } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { ConfigKeys, PricingSimulatorStatusType } from 'utils/Enums';
import _ from 'lodash';

interface LabelWithPopoverProps {
    scenarioId: number;
    name: string;
    status?: PricingSimulatorStatusType;
}

const LabelWithPopover: React.FC<LabelWithPopoverProps> = ({ scenarioId, name, status }) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [data, setData] = useState<any>({});
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const pricingSimulatorConfig = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
    const projectStatuses = pricingSimulatorConfig.projectStatuses;
    const dataSourceList = pricingSimulatorConfig?.projectDataSource;
    const hidePriceEffectiveDateProperty = pricingSimulatorConfig?.hidePriceEffectiveDateProperty;

    //get single project details
    const { loading: singleProjectDetailsLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(scenarioId) && popoverVisible },
        queryKey: ['ps-single-project-details_by_scenario', scenarioId],
        url: `${pricingSimulatorUrls.getSingleProjectDetailsByScenarioIdUrl}/${scenarioId}`,
        variables: {},
        parserFunction: (res: any) => {
            const { start_timestamp, end_timestamp } = res?.simulation_time_period;
            const formattedSimulationStartDate = formatDatePreservingTimeValues(start_timestamp);
            const formattedSimulationEndDate = formatDatePreservingTimeValues(end_timestamp);
            const formattedPriceEffectiveEndDate = formatDatePreservingTimeValues(res?.price_effective_date);
            const parsedData = {
                projectName: res?.project_name,
                projectId: res?.project_id,
                createdBy: res?.created_by,
                status: res?.status,
                dataSource: dataSourceList?.find((item: any) => item?.value === res?.data_source)?.label,
                filters: Object.keys(res.project_filters)
                    .map((dim) => {
                        const filters = res.project_filters[dim];
                        return Object.keys(filters).map((category) => ({
                            name: category,
                            attributeNames: filters[category],
                        }));
                    })
                    .flat(),
                simulationPeriod: `${dayjs(formattedSimulationStartDate)
                    .add(6, 'day')
                    .format(commonConfig?.dateFormatAlt)} - ${dayjs(formattedSimulationEndDate)
                    .add(6, 'day')
                    .format(commonConfig?.dateFormatAlt)}`,
                priceEffectiveDate:
                    res?.price_effective_date &&
                    dayjs(formattedPriceEffectiveEndDate).add(6, 'day').format(commonConfig?.dateFormatAlt),
            };

            setData(parsedData);
            return parsedData;
        },
    });

    const handleVisibleChange = (visible: boolean) => {
        setPopoverVisible(visible);
    };

    const popoverContent = singleProjectDetailsLoading ? (
        <div className="flex justify-center items-center  h-32">
            <Spin />
        </div>
    ) : (
        <div>
            {/* Render data here once it’s loaded */}
            {data ? (
                <div className="flex flex-col gap-2 max-h-[250px] overflow-y-auto font-xs">
                    <div className="flex gap-1 flex-wrap">
                        <Tag
                            style={{ borderRadius: 4 }}
                            color={projectStatuses[status ?? 'DRAFT']?.colorType || 'default'}
                        >
                            {projectStatuses[status ?? 'DRAFT']?.label}
                        </Tag>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                        <span className="text-backgroundGreyAlt7 ">Source:</span>
                        <span>{data?.dataSource}</span>
                    </div>
                    <div className="flex gap-1 ">
                        <span className="text-backgroundGreyAlt7 ">Simulation Period:</span>
                        <span>{data?.simulationPeriod}</span>
                    </div>
                    {!hidePriceEffectiveDateProperty && (
                        <div className="flex gap-1 flex-wrap">
                            <span className="text-backgroundGreyAlt7 ">Price Effective Date:</span>
                            <span>{data?.priceEffectiveDate}</span>
                        </div>
                    )}

                    {data?.filters?.map((filterItem: any) => {
                        return (
                            <div className="flex gap-1 flex-wrap">
                                <span className="text-backgroundGreyAlt7 ">{_.capitalize(filterItem?.name)}:</span>
                                {filterItem.attributeNames?.map((attr: string, index: number) => (
                                    <span key={attr}>
                                        {attr}
                                        {index !== filterItem.attributeNames.length - 1 ? ',' : ''}
                                    </span>
                                ))}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p>No data available.</p>
            )}
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            trigger="click"
            overlayInnerStyle={{
                position: 'relative',
                width: '351px',
            }}
            open={popoverVisible}
            onOpenChange={handleVisibleChange}
            placement="bottomLeft" // Position popover at the bottom
        >
            <Tooltip
                placement="top"
                color="white"
                destroyTooltipOnHide
                overlayStyle={{ borderRadius: 4 }}
                overlayInnerStyle={{
                    color: colorConstants.darkGrey,
                }}
                title={name}
            >
                <span className="text-black h-[75px] cursor-pointer hover:underline underline-offset-4">
                    {/* {(name?.length || 0) > 20 ? `${name?.slice(0, 20)}...` : name} */}
                    {name}
                    {/* temp fix, giving a fixed height to show entire name */}
                </span>
            </Tooltip>
        </Popover>
    );
};

export default LabelWithPopover;
